import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Typography, Box, Grid, CircularProgress } from "@mui/material";
import WorldMap from "react-svg-worldmap";
import countryListData from "country-list/data.json";
import {
  acme,
  acmeNeutralColorScale,
  acmeDashboardStatGridSx,
  acmeDashboardCardContentSx,
  acmeDashboardPanelCardSx,
  acmeDashboardSectionTitleSx,
  acmeDashboardSectionDescSx,
  DASHBOARD_STAT_GRID_SPACING,
  DASHBOARD_CHART_GRID_SPACING,
} from "../../theme/acmeTheme";
import {
  Card,
  CardContent,
  StatCard,
  Button,
  smsMetricIcons,
  DashboardMetricIcon,
} from "../ui";

const countriesMap = {};
countryListData.forEach((country) => {
  if (country?.code && country?.name) {
    countriesMap[country.code.toUpperCase()] = country.name;
  }
});

const metricLabels = {
  campaigns: "Campaigns",
  recipients: "Recipients",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
};

const emptySmsStats = () => ({
  campaigns: { count: 0, rate: 0, loading: false },
  recipients: { count: 0, rate: 0, loading: false },
  sent: { count: 0, rate: 0, loading: false },
  delivered: { count: 0, rate: 0, loading: false },
  failed: { count: 0, rate: 0, loading: false },
});

const panelCardSx = acmeDashboardPanelCardSx;

const DashboardSMS = ({ dateRange }) => {
  const [smsStats, setSmsStats] = useState(emptySmsStats());
  const [selectedMetric, setSelectedMetric] = useState("recipients");
  const [countryData, setCountryData] = useState([]);
  const [countryDataLoading, setCountryDataLoading] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);

  const calculateRate = (value, total) =>
    total > 0 ? Math.round((value / total) * 100) : 0;

  const fetchSmsStats = async () => {
    setSmsStats((prev) =>
      Object.fromEntries(
        Object.keys(prev).map((k) => [k, { ...prev[k], loading: true }]),
      ),
    );
    try {
      const params = {
        startDate: format(dateRange.startDate, "yyyy-MM-dd"),
        endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      };
      const { data } = await axios.get("/api/dashboard/sms-stats", { params });
      const cur = data.current || {};
      const sent = cur.sent || 0;
      const delivered = cur.delivered || 0;
      const failed = cur.failed || 0;
      const recipients = cur.recipients || 0;

      setSmsStats({
        campaigns: { count: cur.campaigns || 0, rate: 0, loading: false },
        recipients: {
          count: recipients,
          rate: calculateRate(sent, recipients),
          loading: false,
        },
        sent: {
          count: sent,
          rate: calculateRate(sent, recipients),
          loading: false,
        },
        delivered: {
          count: delivered,
          rate: calculateRate(delivered, sent),
          loading: false,
        },
        failed: {
          count: failed,
          rate: calculateRate(failed, sent),
          loading: false,
        },
      });
    } catch (error) {
      console.error("Failed to fetch SMS dashboard stats:", error);
      setSmsStats(emptySmsStats());
    }
  };

  const fetchCountryData = async (metric) => {
    setCountryDataLoading(true);
    try {
      const { data } = await axios.get("/api/dashboard/sms-country-stats", {
        params: {
          startDate: format(dateRange.startDate, "yyyy-MM-dd"),
          endDate: format(dateRange.endDate, "yyyy-MM-dd"),
          metric,
        },
      });
      setCountryData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch SMS country stats:", error);
      setCountryData([]);
    } finally {
      setCountryDataLoading(false);
    }
  };

  useEffect(() => {
    fetchSmsStats();
    fetchCountryData(selectedMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedMetric]);

  const deliveryBreakdown = useMemo(() => {
    const sent = smsStats.sent.count;
    const delivered = smsStats.delivered.count;
    const failed = smsStats.failed.count;
    const pending = Math.max(0, sent - delivered - failed);
    return { sent, delivered, failed, pending };
  }, [smsStats]);

  const deliveryRate = calculateRate(
    deliveryBreakdown.delivered,
    deliveryBreakdown.sent,
  );

  const maxCountryValue = Math.max(...countryData.map((d) => d.value), 1);

  const renderStatBox = (title, iconKey, value, rate, loading) => (
    <StatCard
      title={title}
      icon={smsMetricIcons[iconKey]}
      value={value}
      rate={iconKey !== "campaigns" ? rate : 0}
      loading={loading}
      selected={selectedMetric === iconKey}
      onClick={
        iconKey !== "campaigns" ? () => setSelectedMetric(iconKey) : undefined
      }
    />
  );

  const mapData = countryData
    .filter((item) => item?.country && typeof item.value === "number")
    .map((item) => ({
      country: String(item.country).toUpperCase(),
      value: item.value,
    }));

  return (
    <Box>
      <Grid
        container
        spacing={DASHBOARD_STAT_GRID_SPACING}
        sx={acmeDashboardStatGridSx}
      >
        {[
          ["CAMPAIGNS", "campaigns", smsStats.campaigns],
          ["RECIPIENTS", "recipients", smsStats.recipients],
          ["SENT", "sent", smsStats.sent],
          ["DELIVERED", "delivered", smsStats.delivered],
          ["FAILED", "failed", smsStats.failed],
        ].map(([title, key, stat]) => (
          <Grid item xs={6} sm={6} md={2.4} key={key}>
            {renderStatBox(title, key, stat.count, stat.rate, stat.loading)}
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={DASHBOARD_CHART_GRID_SPACING}>
        <Grid item xs={12} md={8}>
          <Card sx={{ ...panelCardSx, overflow: "hidden" }}>
            <CardContent sx={acmeDashboardCardContentSx}>
              <Typography variant="h6" sx={acmeDashboardSectionTitleSx}>
                {metricLabels[selectedMetric]} by Country
              </Typography>
              <Typography sx={acmeDashboardSectionDescSx}>
                Distribution by recipient phone country (from numbering plan)
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  minHeight: { xs: "auto", sm: "400px" },
                  height:
                    showAllCountries && countryData.length > 6
                      ? "auto"
                      : { xs: "auto", sm: "400px" },
                  flexDirection: { xs: "column", sm: "row" },
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    flex: { xs: "1 1 auto", sm: "0 0 35%" },
                    pr: { xs: 0, sm: 2 },
                    maxHeight:
                      showAllCountries && countryData.length > 6
                        ? "none"
                        : { xs: "none", sm: "400px" },
                    overflowY:
                      showAllCountries && countryData.length > 6
                        ? "visible"
                        : { xs: "visible", sm: "auto" },
                    mb: { xs: 3, sm: 0 },
                    minWidth: 0,
                  }}
                >
                  {countryDataLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                      <CircularProgress
                        size={28}
                        sx={{ color: acme.colors.iconDefault }}
                      />
                    </Box>
                  ) : countryData.length === 0 ? (
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: acme.colors.mutedForegroundSolid,
                      }}
                    >
                      No geographic data for this period.
                    </Typography>
                  ) : (
                    <>
                      {(showAllCountries
                        ? countryData
                        : countryData.slice(0, 6)
                      ).map((item, index) => (
                        <Box key={`${item.country}-${index}`} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                            >
                              {item.name ||
                                countriesMap[item.country] ||
                                item.country}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "0.875rem", fontWeight: 700 }}
                            >
                              {item.value}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: "100%",
                              height: 8,
                              bgcolor: acme.colors.muted,
                              borderRadius: acme.radius.sm,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${Math.min(100, (item.value / maxCountryValue) * 100)}%`,
                                height: "100%",
                                bgcolor:
                                  acmeNeutralColorScale[5 + Math.min(3, index)],
                              }}
                            />
                          </Box>
                        </Box>
                      ))}
                      {countryData.length > 6 && (
                        <Button
                          variant="link"
                          onClick={() => setShowAllCountries(!showAllCountries)}
                        >
                          {showAllCountries
                            ? "Show less"
                            : `Show all (${countryData.length})`}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    flex: { xs: "1 1 auto", sm: "0 0 65%" },
                    height: { xs: "300px", sm: "100%" },
                    overflow: "hidden",
                    minHeight: { xs: "300px", sm: "400px" },
                    minWidth: 0,
                  }}
                >
                  {countryDataLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CircularProgress
                        size={28}
                        sx={{ color: acme.colors.iconDefault }}
                      />
                    </Box>
                  ) : mapData.length > 0 ? (
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <WorldMap
                        color={acme.colors.iconDefault}
                        data={mapData}
                        valueSuffix=""
                        size="lg"
                        backgroundColor={acme.colors.background}
                        strokeOpacity={0.8}
                        borderColor={acme.colors.border}
                        containerStyle={{
                          width: "100%",
                          height: "100%",
                          maxWidth: "100%",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          color: acme.colors.mutedForegroundSolid,
                        }}
                      >
                        Map unavailable — no data
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={panelCardSx}>
            <CardContent sx={acmeDashboardCardContentSx}>
              <Typography variant="h6" sx={acmeDashboardSectionTitleSx}>
                Delivery breakdown
              </Typography>
              <Typography sx={acmeDashboardSectionDescSx}>
                Share of sent messages by delivery outcome
              </Typography>
              <Box display="flex" justifyContent="center" my={4}>
                <Box position="relative" width={200} height={200}>
                  <svg width="100%" height="100%" viewBox="0 0 200 200">
                    <circle
                      cx="100"
                      cy="100"
                      r="90"
                      fill="none"
                      stroke={acme.colors.muted}
                      strokeWidth="20"
                    />
                    <circle
                      cx="100"
                      cy="100"
                      r="90"
                      fill="none"
                      stroke={acme.colors.brand}
                      strokeWidth="20"
                      strokeDasharray={`${deliveryRate * 5.65} 565`}
                      transform="rotate(-90 100 100)"
                    />
                  </svg>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "1.75rem", sm: "2rem" },
                        fontWeight: 700,
                        color: acme.colors.iconEmphasis,
                      }}
                    >
                      {deliveryRate}%
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: acme.colors.mutedForegroundSolid,
                      }}
                    >
                      delivered
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                display="flex"
                justifyContent="space-around"
                flexWrap="wrap"
                gap={2}
              >
                {[
                  {
                    label: "Delivered",
                    value: deliveryBreakdown.delivered,
                    color: acme.colors.brand,
                  },
                  {
                    label: "Failed",
                    value: deliveryBreakdown.failed,
                    color: acme.colors.destructive,
                  },
                  {
                    label: "Pending",
                    value: deliveryBreakdown.pending,
                    color: acme.colors.border,
                  },
                ].map((row) => (
                  <Box key={row.label} textAlign="center">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mb={0.5}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: row.color,
                          mr: 1,
                        }}
                      />
                      <Typography sx={{ fontSize: "0.875rem" }}>
                        {row.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {deliveryBreakdown.sent === 0 && !smsStats.sent.loading && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  mt={2}
                  sx={{ color: acme.colors.mutedForegroundSolid }}
                >
                  <DashboardMetricIcon name="warning" size={16} tone="muted" />
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    No SMS sent in this date range yet.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardSMS;
